import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/styles/Setup.css';
import { getUserProfile, saveUserProfile } from '../utils/profileUtils';
import { useAuth } from '../components/auth/AuthProvider';
import { useDynamoUtils } from '../context/DynamoUtilsContext';
import { CURRICULUM_MAP } from '../config';

const Setup = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { user } = auth || {};
  const userId = user?.sub || null;
  const dynamoUtilsRef = useDynamoUtils();

  const [formData, setFormData] = useState({
    name: '',
    state: 'NSW', // Default state
    yearMathCombination: '', // Combined year_mathType selection
    textbook: '', // No default selection
    confidenceLevel: 'medium',
    interests: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [availableCurriculums, setAvailableCurriculums] = useState({});
  const [showSupportedConfigs, setShowSupportedConfigs] = useState(false);

  // State-specific textbook configurations
  const getTextbooksForState = (state) => {
    const textbooksByState = {
      NSW: [
        { id: 'pearson', name: 'Pearson', available: false },
        { id: 'oxford', name: 'Oxford', available: false },
        { id: 'jacaranda', name: 'Jacaranda', available: false },
        { id: 'newCenturyMaths', name: 'New Century Maths', available: false },
        { id: 'cambridge', name: 'Cambridge (NSW)', available: true },
        { id: 'nelson', name: 'Nelson (Math in Focus)', available: true },
        { id: 'mathsInFocus', name: 'Maths in Focus', available: false },
        { id: 'general', name: 'General Syllabus Mode', available: true }
      ],
      QLD: [
        { id: 'jacaranda', name: 'Jacaranda', available: false },
        { id: 'nelson', name: 'Nelson', available: false },
        { id: 'essentialMathematics', name: 'Essential Mathematics', available: false },
        { id: 'pearson', name: 'Pearson', available: false },
        { id: 'cambridge', name: 'Cambridge (QLD)', available: false },
        { id: 'general', name: 'General Syllabus Mode', available: false }
      ]
    };
    
    return textbooksByState[state] || [];
  };

  // Parse available curriculums from tutorConfig
  useEffect(() => {
    // Process available curricula from MODULES_BY_TEXTBOOK using state as the top level key
    const available = {};
    
    // Check if the selected state exists in CURRICULUM_MAP
    if (CURRICULUM_MAP[formData.state]) {
      Object.keys(CURRICULUM_MAP[formData.state]).forEach(textbook => {
        available[textbook] = {};
        Object.keys(CURRICULUM_MAP[formData.state][textbook] || {}).forEach(curriculum => {
          // Extract year level and math type from curriculum key (e.g., "11_standard")
          const parts = curriculum.split('_');
          if (parts.length === 2) {
            const [year, mathType] = parts;
            
            if (!available[textbook][year]) {
              available[textbook][year] = [];
            }
            
            available[textbook][year].push(mathType);
          }
        });
      });
    }
    
    setAvailableCurriculums(available);
  }, [formData.state]); // Re-run when state changes

  // Load user profile on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoading(true);
        
        // Use authenticated userId directly instead of generating one
        if (userId) {
          const profile = await getUserProfile({ userId }, dynamoUtilsRef);
          
          if (profile) {
            // Ensure we have a state field in the loaded profile, default to NSW if not present
            const updatedProfile = {
              ...profile,
              state: profile.state || 'NSW'
            };
            
            // Convert old format (yearLevel + mathsType) to new format (yearMathCombination)
            if (profile.yearLevel && profile.mathsType && !profile.yearMathCombination) {
              updatedProfile.yearMathCombination = `${profile.yearLevel}_${profile.mathsType}`;
              // Clean up old fields for new format
              delete updatedProfile.yearLevel;
              delete updatedProfile.mathsType;
            }
            
            setFormData(updatedProfile);
            
            // If the profile was updated to add state or convert format, save it back
            if (!profile.state || (profile.yearLevel && profile.mathsType && !profile.yearMathCombination)) {
              await saveUserProfile(updatedProfile, userId, dynamoUtilsRef);
            }
          }
        } else {
          console.warn('No authenticated user found - cannot load profile');
        }
      } catch (e) {
        console.error('Error loading profile:', e);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userId, auth, dynamoUtilsRef]);

  // Generate combined year level and math type options
  const getCombinedYearMathOptions = (state) => {
    const baseYears = ['5', '6', '7', '8', '9', '10', '11', '12'];
    const options = [];
    
    baseYears.forEach(year => {
      const mathTypes = getMathTypes(state, year);
      
      mathTypes.forEach(mathType => {
        // Create a combined ID and display name
        const combinedId = `${year}_${mathType.id}`;
        let displayName;
        
        if (mathTypes.length === 1 && mathType.id === 'general') {
          // For years with only general math, just show "Year X"
          displayName = `Year ${year}`;
        } else {
          // For years with multiple math types, show "Year X - Math Type"
          displayName = `Year ${year} - ${mathType.name}`;
        }
        
        options.push({
          id: combinedId,
          year,
          mathType: mathType.id,
          name: displayName
        });
      });
    });
    
    return options;
  };

  // Define math types based on the state and year level
  const getMathTypes = (state, yearLevel) => {
    // NSW and QLD have similar structures for senior years
    if (state === 'NSW') {
      if (yearLevel === '11') {
        return [
          { id: 'standard', name: 'Standard Mathematics' },
          { id: 'advanced', name: 'Advanced Mathematics' },
          { id: 'extension1', name: 'Extension 1' }
        ];
      } else if (yearLevel === '12') {
        return [
          { id: 'standard', name: 'Standard Mathematics' },
          { id: 'advanced', name: 'Advanced Mathematics' },
          { id: 'extension1', name: 'Extension 1' },
          { id: 'extension2', name: 'Extension 2' }
        ];
      } else {
        // For other year levels, we just have a general mathematics course
        return [
          { id: 'general', name: 'Mathematics' }
        ];
      }
    } else if (state === 'QLD') {
      if (yearLevel === '11' || yearLevel === '12') {
        return [
          { id: 'general', name: 'General Mathematics' },
          { id: 'mathematical', name: 'Mathematical Methods' },
          { id: 'specialist', name: 'Specialist Mathematics' }
        ];
      } else {
        return [
          { id: 'general', name: 'Mathematics' }
        ];
      }
    }
    
    // Fallback for other states
    return [
      { id: 'general', name: 'Mathematics' }
    ];
  };

  const states = [
    { id: 'NSW', name: 'New South Wales' },
    { id: 'QLD', name: 'Queensland' }
  ];

  // Check if a specific year level has any content in any textbook
  const isYearMathCombinationAvailable = (yearMathCombination) => {
    if (!yearMathCombination) return false;
    
    const [year, mathType] = yearMathCombination.split('_');
    if (!year || !mathType) return false;
    
    return Object.keys(availableCurriculums).some(textbook => 
      availableCurriculums[textbook] && 
      availableCurriculums[textbook][year] &&
      availableCurriculums[textbook][year].includes(mathType)
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => {
      const updatedData = { ...prevData, [name]: value };
      
      // If state changes, reset ALL dependent fields to empty/default values
      if (name === 'state') {
        return {
          ...prevData,
          state: value,
          yearMathCombination: '', // Clear combined selection
          textbook: '' // Clear textbook selection
        };
      }
      
      // If year/math combination changes, reset textbook selection
      if (name === 'yearMathCombination') {
        updatedData.textbook = '';
      }
      
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      // Ensure we have a valid userId before saving
      if (!userId) {
        setError('No authenticated user found. Please log in again.');
        return;
      }
      
      // Validation: Check if all required fields are filled for supported states
      if (isSupportedState) {
        if (!formData.name.trim()) {
          setError('Please enter your name.');
          return;
        }
        if (!formData.yearMathCombination) {
          setError('Please select a year level and mathematics type.');
          return;
        }
        if (!formData.textbook) {
          setError('Please select a textbook.');
          return;
        }
        if (!formData.confidenceLevel) {
          setError('Please select your confidence level.');
          return;
        }
      } else {
        // For unsupported states, only require name
        if (!formData.name.trim()) {
          setError('Please enter your name.');
          return;
        }
      }
      
      // Use our utility function to save to localStorage and DynamoDB with the authenticated userId
      const success = await saveUserProfile(formData, userId, dynamoUtilsRef);
      
      if (success) {
        // Navigate to the tutor session page or home page
        navigate('/');
      } else {
        setError('Failed to save your profile. Please try again.');
      }
    } catch (e) {
      console.error('Error saving profile:', e);
      setError('Failed to save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Generate all currently supported configurations
  const getAllSupportedConfigurations = () => {
    const supportedConfigs = [];
    
    // Check each state
    Object.keys(CURRICULUM_MAP).forEach(state => {
      const stateName = states.find(s => s.id === state)?.name || state;
      
      // Check each textbook in this state
      Object.keys(CURRICULUM_MAP[state]).forEach(textbook => {
        const textbookConfig = getTextbooksForState(state).find(t => t.id === textbook);
        const textbookName = textbookConfig?.name || textbook;
        
        // Only include if textbook is marked as available
        if (textbookConfig?.available) {
          // Check each curriculum combination
          Object.keys(CURRICULUM_MAP[state][textbook] || {}).forEach(curriculum => {
            const parts = curriculum.split('_');
            if (parts.length === 2) {
              const [year, mathType] = parts;
              const mathTypes = getMathTypes(state, year);
              const mathTypeName = mathTypes.find(mt => mt.id === mathType)?.name || mathType;
              
              let displayName;
              if (mathTypes.length === 1 && mathType === 'general') {
                displayName = `Year ${year}`;
              } else {
                displayName = `Year ${year} - ${mathTypeName}`;
              }
              
              supportedConfigs.push({
                state: stateName,
                yearMath: displayName,
                textbook: textbookName,
                fullConfig: `${stateName} - ${displayName} - ${textbookName}`
              });
            }
          });
        }
      });
    });
    
    return supportedConfigs.sort((a, b) => a.fullConfig.localeCompare(b.fullConfig));
  };

  if (loading) {
    return (
      <div className="setup-container">
        <div className="setup-content">
          <div className="setup-loader">
            <div className="loader-spinner"></div>
            <h2>Loading your profile...</h2>
          </div>
        </div>
      </div>
    );
  }

  const selectedYearMathOptions = getCombinedYearMathOptions(formData.state);
  const availableTextbooks = getTextbooksForState(formData.state);
  const isSupportedState = ['NSW', 'QLD'].includes(formData.state);
  
  // Check if the form is complete and valid for submission
  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    if (!formData.confidenceLevel) return false;
    
    if (isSupportedState) {
      if (!formData.yearMathCombination) return false;
      if (!formData.textbook) return false;
    }
    
    return true;
  };

  return (
    <div className="setup-container">
      <div className="setup-content">
        <div className="setup-header">
          <h1>Your Learning Profile</h1>
          <p>Tell us about yourself so we can personalize your tutoring experience</p>
        </div>

        {/* Supported Configurations Dropdown */}
        <div className="supported-configs-section">
          <div className="supported-configs-header" onClick={() => setShowSupportedConfigs(!showSupportedConfigs)}>
            <h3>Currently Supported Configurations</h3>
            <span className={`dropdown-arrow ${showSupportedConfigs ? 'open' : ''}`}>▼</span>
          </div>
          {showSupportedConfigs && (
            <div className="supported-configs-list">
              <p className="supported-configs-intro">
                Here are all the curriculum combinations we currently support:
              </p>
              <div className="supported-configs-grid">
                {getAllSupportedConfigurations().map((config, index) => (
                  <div key={index} className="supported-config-item">
                    <div className="config-state">{config.state}</div>
                    <div className="config-year-math">{config.yearMath}</div>
                    <div className="config-textbook">{config.textbook}</div>
                  </div>
                ))}
              </div>
              {getAllSupportedConfigurations().length === 0 && (
                <p className="no-configs-message">No supported configurations found.</p>
              )}
            </div>
          )}
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-group">
            <label htmlFor="name">Your Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="state">Your State/Area</label>
            <div className="state-area-grid">
              {states.map(state => (
                <div 
                  className={`state-area-card ${formData.state === state.id ? 'selected' : ''} ${state.comingSoon ? 'disabled' : ''}`}
                  key={state.id}
                >
                  <input
                    type="radio"
                    id={`state-${state.id}`}
                    name="state"
                    value={state.id}
                    checked={formData.state === state.id}
                    onChange={handleChange}
                    disabled={state.comingSoon}
                    required
                    className="state-area-input"
                  />
                  <label htmlFor={`state-${state.id}`} className="state-area-label">
                    <span className="state-area-name">{state.name}</span>
                    {state.comingSoon && <span className="coming-soon">Coming soon</span>}
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          {/* Only show curriculum options if a supported state is selected */}
          {isSupportedState && (
            <>
              <div className="form-section">
                <h3>Your Curriculum</h3>
                
                <div className="form-group">
                  <label className="form-label">Year Level & Mathematics Type</label>
                  <div className="year-levels-grid">
                    {selectedYearMathOptions.map(option => {
                      // Check if this year/math combination has any available content
                      const isSupported = isYearMathCombinationAvailable(option.id);
                      
                      return (
                        <div 
                          className={`year-level-card ${formData.yearMathCombination === option.id ? 'selected' : ''} ${!isSupported ? 'disabled' : ''}`}
                          key={option.id}
                        >
                          <input
                            type="radio"
                            id={`yearmth-${option.id}`}
                            name="yearMathCombination"
                            value={option.id}
                            checked={formData.yearMathCombination === option.id}
                            onChange={handleChange}
                            disabled={!isSupported}
                            required
                            className="year-level-input"
                          />
                          <label htmlFor={`yearmth-${option.id}`} className="year-level-label">
                            <span className="year-level-name">{option.name}</span>
                            {!isSupported && <span className="coming-soon">Coming soon</span>}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  {!formData.yearMathCombination && (
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      Please select your year level and mathematics type to continue
                    </p>
                  )}
                </div>
              </div>
              
              {formData.yearMathCombination && (
                <div className="form-section">
                  <h3>Your Learning Resources</h3>
                  
                  <div className="form-group">
                    <label className="form-label">Your Textbook</label>
                    <div className="textbooks-grid">
                      {availableTextbooks.map(textbook => {
                        // Check if this textbook has content for the selected year/math combination
                        let isAvailable = textbook.available;
                        
                        if (isAvailable && formData.yearMathCombination) {
                          // Check if the textbook has content for the specific year/math combination
                          isAvailable = CURRICULUM_MAP[formData.state] && 
                            CURRICULUM_MAP[formData.state][textbook.id] && 
                            CURRICULUM_MAP[formData.state][textbook.id][formData.yearMathCombination];
                        }
                        
                        return (
                          <div 
                            className={`textbook-card ${formData.textbook === textbook.id ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                            key={textbook.id}
                          >
                            <input
                              type="radio"
                              id={textbook.id}
                              name="textbook"
                              value={textbook.id}
                              checked={formData.textbook === textbook.id}
                              onChange={handleChange}
                              disabled={!isAvailable}
                              required
                              className="textbook-input"
                            />
                            <label htmlFor={textbook.id} className="textbook-label">
                              <span className="textbook-name">{textbook.name}</span>
                              {!isAvailable && <span className="coming-soon">Coming soon</span>}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                    {!formData.textbook && (
                      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        Please select a textbook to complete your setup
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Show a message if a non-supported state is selected */}
          {!isSupportedState && (
            <div className="state-coming-soon-message">
              <div className="info-icon">ⓘ</div>
              <p>Support for {states.find(s => s.id === formData.state)?.name || formData.state} is coming soon! Currently, only New South Wales (NSW) and Queensland (QLD) curriculums are available.</p>
            </div>
          )}
          
          <div className="form-section">
            <h3>Your Learning Style</h3>
            
            <div className="form-group">
              <label className="form-label">How do you feel about mathematics?</label>
              <div className="confidence-options">
                {[
                  { id: 'low', name: 'I struggle with math concepts' },
                  { id: 'medium', name: 'I understand most concepts but need practice' },
                  { id: 'high', name: 'I feel confident with most math topics' }
                ].map(level => (
                  <div 
                    className={`confidence-card ${formData.confidenceLevel === level.id ? 'selected' : ''}`}
                    key={level.id}
                  >
                    <input
                      type="radio"
                      id={level.id}
                      name="confidenceLevel"
                      value={level.id}
                      checked={formData.confidenceLevel === level.id}
                      onChange={handleChange}
                      required
                      className="confidence-input"
                    />
                    <label htmlFor={level.id} className="confidence-label">
                      <span className="confidence-name">{level.name}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="interests" className="form-label">What topics are you most interested in or need help with?</label>
              <textarea
                id="interests"
                name="interests"
                value={formData.interests}
                onChange={handleChange}
                placeholder="E.g. Quadratic equations, trigonometry, probability..."
                rows="3"
                className="form-control"
              ></textarea>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={saving || !isFormValid()}
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
          
        </form>
      </div>
    </div>
  );
};

export default Setup;